import { FieldSelect } from 'components/redesign/FieldSelect';
import { FieldTextarea } from 'components/redesign/FieldTextArea';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import useMediaQuery from 'hooks/useMediaQuery';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { getExecutorsPageFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getExecutorsPageFilters';
import { FtsOrganizationService } from 'services/FtsOrganizationService';
import { Button } from 'ui/redesign/Button';
import { ButtonVariant } from 'ui/redesign/Button/constants';
import { OptionType } from 'ui/redesign/Select/types';
import { Spinner } from 'ui/Spinner';
import { IS_SMALL_DEVICE } from 'utils/settings';
import { FULL_NAME_REGEXP, initialMyExecutorsFormFilters } from './consts';
import { MyExecutorsFiltersFormProps, MyExecutorsFiltersFormValues } from './types';
import { FieldInput } from 'components/redesign/FieldInput';

export const MyExecutorsFiltersModal = ({ onFilter }: MyExecutorsFiltersFormProps) => {
  const filters = useAppSelector(getExecutorsPageFilters);

  const dispatch = useAppDispatch();
  const isSmall = useMediaQuery(IS_SMALL_DEVICE);
  const { control, handleSubmit, reset } = useForm<MyExecutorsFiltersFormValues>({
    defaultValues: { ...filters },
  });

  const { data, isFetching } = useQuery(['getFtsStatus'], () =>
    FtsOrganizationService.getFtsStatus()
  );

  const statuses = (data?.data ?? []).map((item) => {
    const statusOptions: OptionType<string> = {
      value: String(item.id) ?? '',
      label: item.name,
    };
    return statusOptions;
  });

  const onCloseFilters = () => {
    dispatch(modalActions.closeModal());
  };

  const onResetFilter = useCallback(() => {
    reset(initialMyExecutorsFormFilters);
  }, [reset]);

  const submitFiltersHandler = (values: MyExecutorsFiltersFormValues) => {
    onFilter({
      fullName: values.fullName,
      ftsStatusName: values.ftsStatusName,
      tin: values.tin,
    });
    dispatch(modalActions.closeModal());
  };

  // Маленький экран
  if (isSmall) {
    return (
      <div className="-mt-4 flex w-full flex-col">
        <div className="mb-4 flex w-full cursor-pointer items-center justify-center gap-2">
          <form
            className="flex h-full w-full flex-col p-4"
            onSubmit={handleSubmit(submitFiltersHandler)}
          >
            <div className="flex grow flex-col gap-4">
              <div className="flex justify-between">
                <span className="title2-medium">Фильтр</span>
                <button className="mr-2 !text-primary-60" type="button" onClick={onResetFilter}>
                  Очистить
                </button>
              </div>
              {isFetching ? (
                <Spinner />
              ) : (
                <>
                  <FieldTextarea
                    name="fullName"
                    control={control}
                    label="ФИО исполнителя"
                    isClearable={false}
                    minLength={2}
                    maxLength={600}
                    inputPattern={FULL_NAME_REGEXP}
                  />
                  <FieldInput
                    name="tin"
                    control={control}
                    label="ИНН"
                    isClearable={false}
                    minLength={2}
                    maxLength={12}
                    type="number"
                  />
                  <FieldSelect
                    control={control}
                    name="ftsStatusName"
                    label="Статус ФНС"
                    options={statuses}
                    variant="filter"
                    isMulti
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                      option: (base) => ({ ...base, color: 'black' }),
                    }}
                    menuPortalTarget={document.body}
                  />
                </>
              )}
            </div>
            <div className="fixed bottom-10 w-full pr-8">
              <Button className="mb-6 w-full max-w-[640px]" type="submit">
                ПРИМЕНИТЬ ФИЛЬТРЫ
              </Button>
              <Button
                variant={ButtonVariant.OUTLINE}
                className="btn-ghost w-full max-w-[640px] self-center"
                type="button"
                onClick={onCloseFilters}
              >
                ЗАКРЫТЬ
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  } else {
    return (
      <form className="relative flex flex-col gap-6" onSubmit={handleSubmit(submitFiltersHandler)}>
        <div className="flex grow flex-col gap-4">
          <div className="flex justify-between">
            <span className="title2-medium text-text-100">Фильтр</span>
            <button className="mr-2 !text-primary-60" type="button" onClick={onResetFilter}>
              Очистить
            </button>
          </div>
          {isFetching ? (
            <Spinner />
          ) : (
            <>
              <FieldTextarea
                name="fullName"
                control={control}
                label="ФИО исполнителя"
                isClearable={false}
                minLength={2}
                maxLength={600}
                inputPattern={FULL_NAME_REGEXP}
              />
              <FieldInput
                name="tin"
                control={control}
                label="ИНН"
                isClearable={false}
                minLength={2}
                maxLength={12}
                type="number"
              />
              <FieldSelect
                control={control}
                name="ftsStatusName"
                label="Статус ФНС"
                options={statuses}
                variant="filter"
                isMulti
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                  option: (base) => ({ ...base, color: 'black' }),
                }}
                menuPortalTarget={document.body}
              />
            </>
          )}
        </div>

        <div className="flex items-center justify-end">
          <Button type="submit">ПРИМЕНИТЬ</Button>
        </div>
      </form>
    );
  }
};
